import { useTranslation } from "react-i18next";
import { NavigateFunction, useNavigate } from "react-router-dom";

import { REACTIONS, Reaction, Release } from "../../services/releases";
import { Body, Card, Cta, Face, Kind, Media, Reactions } from "./styles";

const prefix = "pages.releases";

const FACES: Record<Reaction, string> = { sad: "😞", neutral: "😐", happy: "😃" };

/** "25 de out. de 2026" / "Oct 25, 2026" */
export const releaseDate = (iso: string, lang: string) => {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? "" : new Intl.DateTimeFormat(lang, { dateStyle: "medium" }).format(date);
};

// The button's address: the editor's own screens open here (without loading the editor again), anything else in a new
// tab. A click with a modifier (a new tab, a new window) is left to the browser
const ctaProps = (url: string, navigate: NavigateFunction) => {
  if (!url.startsWith(`${window.location.origin}/`)) {
    return { target: "_blank", rel: "noopener noreferrer" };
  }
  return {
    onClick: (event: React.MouseEvent<HTMLAnchorElement>) => {
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      const to = new URL(url);
      navigate(to.pathname + to.search + to.hash);
    },
  };
};

const prefersReducedMotion = () =>
  typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

type Props = {
  release: Release;
  /** A reaction being sent (the faces wait) */
  reacting?: boolean;
  /** Thanks or an error, after a reaction */
  status?: string;
  onReact: (reaction: Reaction) => void;
};

/** A release with its whole text, as on keepo.bio/novidades: its kind and date, the title, the picture or video,
 * the text, the button and the faces. */
export const ReleaseCard = ({ release, reacting, status, onReact }: Props) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const titleId = `release-${release.slug}`;
  const alt = release.image?.alt || release.title;

  return (
    <Card aria-labelledby={titleId}>
      <header>
        <div className="meta">
          <Kind $kind={release.kind}>{t(`${prefix}.kind.${release.kind}`)}</Kind>
          <time dateTime={release.published_at}>{releaseDate(release.published_at, i18n.language)}</time>
        </div>
        <h2 id={titleId}>{release.title}</h2>
      </header>

      {release.image && (
        <Media>
          {release.video_url ? (
            <video
              src={release.video_url}
              poster={release.image.url}
              width={release.image.width ?? undefined}
              height={release.image.height ?? undefined}
              aria-label={alt}
              muted
              loop
              playsInline
              autoPlay={!prefersReducedMotion()}
              preload="metadata"
            />
          ) : (
            <img
              src={release.image.url}
              alt={alt}
              width={release.image.width ?? undefined}
              height={release.image.height ?? undefined}
              loading="lazy"
            />
          )}
        </Media>
      )}

      {/* cleaned by the API (only text markup), as the site does */}
      {release.body_html && <Body dangerouslySetInnerHTML={{ __html: release.body_html }} />}

      {release.cta && (
        <Cta href={release.cta.url} {...ctaProps(release.cta.url, navigate)}>
          {release.cta.label}
        </Cta>
      )}

      <Reactions>
        <p className="question" id={`${titleId}-reactions`}>
          {t(`${prefix}.reactions.question`)}
        </p>
        <div className="faces" role="group" aria-labelledby={`${titleId}-reactions`}>
          {REACTIONS.map(reaction => (
            <Face
              key={reaction}
              type="button"
              aria-pressed={release.reaction === reaction}
              aria-label={t(`${prefix}.reactions.${reaction}`)}
              title={t(`${prefix}.reactions.${reaction}`)}
              disabled={reacting}
              onClick={() => release.reaction !== reaction && onReact(reaction)}>
              <span aria-hidden="true">{FACES[reaction]}</span>
            </Face>
          ))}
        </div>
        <p className="status" role="status" aria-live="polite">
          {status}
        </p>
      </Reactions>
    </Card>
  );
};
